import React from "react";
import {
  Box,
  Container,
  Heading,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";

const sections = [
  {
    number: "01",
    title: "Scope of this notice",
    content: (
      <>
        <Text>
          This Privacy Notice explains how Serena Space handles personal
          information collected through the Serena Space institutional website
          and related website interactions.
        </Text>
        <Text>
          It applies to visitors, prospective partners, researchers, members
          of the Serena Space community, and other individuals who interact
          with this website.
        </Text>
        <Text>
          This notice does not, by itself, govern personal information
          processed through Serena Space research studies, clinical or
          implementation activities, or separate health technologies such as
          Selense. Those activities may be subject to additional privacy
          notices, consent processes, agreements, or other safeguards
          appropriate to the nature of the information and activity involved.
        </Text>
      </>
    ),
  },
  {
    number: "02",
    title: "Who we are",
    content: (
      <>
        <Text>
          Serena Space is a health science and technology organisation focused
          on building the scientific, technological and translational
          infrastructure needed to advance preventive and precision healthcare
          in Africa.
        </Text>
        <Text>
          For personal information processed through this institutional
          website, Serena Space acts as the organisation responsible for
          determining the purposes and means of the relevant processing,
          subject to the applicable legal and contractual context.
        </Text>
      </>
    ),
  },
  {
    number: "03",
    title: "Information we may collect",
    content: (
      <>
        <Text>
          The information we collect depends on how you interact with the
          website. This may include:
        </Text>

        <Box pl={5}>
          <Stack spacing={3}>
            <Text>
              <strong>Information you provide directly:</strong> such as your
              name, email address, organisation, professional role, and the
              contents of enquiries or partnership communications you send to
              us.
            </Text>

            <Text>
              <strong>Engagement information:</strong> information associated
              with your interaction with resources, events, enquiries, or
              other institutional activities where such information is
              collected.
            </Text>

            <Text>
              <strong>Technical information:</strong> information that may be
              generated automatically when you access a website, such as
              browser, device, network, security, and server-log information,
              where applicable to the technologies we operate.
            </Text>
          </Stack>
        </Box>

        <Text>
          We seek to collect information that is relevant and proportionate
          to the purpose for which it is being processed.
        </Text>
      </>
    ),
  },
  {
    number: "04",
    title: "How we use information",
    content: (
      <>
        <Text>
          Depending on the nature of the interaction, we may use personal
          information to:
        </Text>

        <Box pl={5}>
          <Stack spacing={3}>
            <Text>• respond to enquiries and requests;</Text>
            <Text>
              • communicate with prospective or existing research,
              institutional, technology, funding, or implementation partners;
            </Text>
            <Text>
              • facilitate participation in relevant Serena Space activities,
              including scientific exchange and institutional engagement;
            </Text>
            <Text>
              • provide access to information and resources requested through
              the website;
            </Text>
            <Text>
              • maintain the security, integrity, and functionality of the
              website;
            </Text>
            <Text>
              • understand and improve how the website and its institutional
              resources are used; and
            </Text>
            <Text>
              • comply with applicable legal, regulatory, contractual, or
              legitimate organisational requirements.
            </Text>
          </Stack>
        </Box>
      </>
    ),
  },
  {
    number: "05",
    title: "Lawful basis for processing",
    content: (
      <>
        <Text>
          Where applicable, Serena Space processes personal information on a
          lawful basis recognised under applicable data-protection law. The
          appropriate basis depends on the nature and purpose of the
          processing.
        </Text>
        <Text>
          Depending on the circumstances, this may include consent,
          performance of a contract or steps taken at an individual's
          request, compliance with a legal obligation, or another lawful
          basis recognised by applicable law.
        </Text>
        <Text>
          Where processing relies on consent, individuals may withdraw that
          consent subject to any applicable legal or contractual limitations.
          Withdrawal does not necessarily affect the lawfulness of processing
          carried out before the withdrawal.
        </Text>
      </>
    ),
  },
  {
    number: "06",
    title: "Health, research, and sensitive information",
    content: (
      <>
        <Text>
          Serena Space operates in areas that can involve health, scientific,
          research, and other potentially sensitive information. We recognise
          that such information requires particular care.
        </Text>
        <Text>
          The institutional website is not intended to collect health,
          clinical, genetic, or genomic information from visitors as a
          condition of ordinary website use.
        </Text>
        <Text>
          Where Serena Space conducts research, implementation activities, or
          operates a health technology that involves such information,
          additional privacy information, consent processes, governance
          controls, or participant agreements may apply to that activity.
        </Text>
        <Text>
          Individuals should therefore rely on the privacy notice or consent
          documentation specifically provided for the relevant study, service,
          or technology where applicable.
        </Text>
      </>
    ),
  },
  {
    number: "07",
    title: "Selense and other Serena technologies",
    content: (
      <>
        <Text>
          Serena Space develops and operates health technologies separately
          from this institutional website. These technologies may process
          information in ways that are substantially different from ordinary
          website interactions.
        </Text>
        <Text>
          For example, Selense is designed around persistent health
          information relating to a loved one. Its data practices therefore
          require product-specific privacy and safety documentation.
        </Text>
        <Text>
          This notice should not be interpreted as a complete privacy notice
          for Selense or any other Serena Space technology.
        </Text>
        <Link
          href="https://selense.serenaspace.com"
          isExternal
          fontWeight="600"
          color="#087F73"
        >
          Visit Selense
        </Link>
      </>
    ),
  },
  {
    number: "08",
    title: "Sharing and third-party services",
    content: (
      <>
        <Text>
          Serena Space does not treat the information entrusted to us as
          something to be freely disclosed or sold.
        </Text>
        <Text>
          We may use service providers or other authorised third parties where
          necessary to operate the website, provide requested services,
          maintain infrastructure, support communications, protect security,
          or fulfil another legitimate purpose.
        </Text>
        <Text>
          Where third parties process personal information on our behalf, we
          seek to ensure that the relevant processing is appropriately
          governed and limited to the purposes for which the information is
          provided.
        </Text>
        <Text>
          We may also disclose information where required or permitted by
          applicable law, regulation, legal process, or to protect the rights,
          security, and integrity of Serena Space, our users, or others.
        </Text>
      </>
    ),
  },
  {
    number: "09",
    title: "Cookies and technical technologies",
    content: (
      <>
        <Text>
          Websites may use cookies, local storage, server logs, or similar
          technologies to provide functionality, maintain security, remember
          preferences, or understand website activity.
        </Text>
        <Text>
          The specific technologies used by Serena Space may change as the
          website develops. Where non-essential tracking or similar
          technologies are introduced, we will provide appropriate information
          and choices where required by applicable law.
        </Text>
        <Text>
          Your browser may also provide controls for managing certain cookies
          and related technologies.
        </Text>
      </>
    ),
  },
  {
    number: "10",
    title: "Security",
    content: (
      <>
        <Text>
          We take reasonable and appropriate measures to protect personal
          information against unauthorised access, disclosure, alteration,
          loss, or destruction, taking into account the nature of the
          information and the risks associated with its processing.
        </Text>
        <Text>
          No method of transmission or storage can be guaranteed to be
          completely secure. We therefore encourage individuals to avoid
          sending sensitive information through ordinary website enquiries
          unless specifically requested through an appropriate Serena Space
          process.
        </Text>
      </>
    ),
  },
  {
    number: "11",
    title: "Retention",
    content: (
      <>
        <Text>
          We retain personal information for no longer than reasonably
          necessary for the purpose for which it was collected, unless a
          longer period is required or permitted by applicable law, regulation,
          contractual obligations, legitimate organisational requirements, or
          another lawful basis.
        </Text>
        <Text>
          Retention periods may therefore differ depending on the nature of the
          information and the relationship or activity to which it relates.
        </Text>
      </>
    ),
  },
  {
    number: "12",
    title: "Your rights",
    content: (
      <>
        <Text>
          Subject to applicable law and any relevant exceptions, individuals
          may have rights concerning their personal information, including
          rights to be informed about processing, request access to personal
          information, seek correction of inaccurate information, object to
          certain processing, request restriction or deletion in appropriate
          circumstances, and exercise other rights recognised under applicable
          data-protection law.
        </Text>
        <Text>
          Requests concerning personal information should be sent to:
        </Text>

        <Link
          href="mailto:support@serenaspace.com"
          fontWeight="600"
          color="#087F73"
        >
          support@serenaspace.com
        </Link>

        <Text>
          We may need to verify the identity of a requester before acting on a
          request and may apply lawful limitations or exemptions where
          applicable.
        </Text>
      </>
    ),
  },
  {
    number: "13",
    title: "Complaints and remediation",
    content: (
      <>
        <Text>
          If you have a concern about how Serena Space has handled your
          personal information, we encourage you to contact us first so that we
          can investigate and seek to address the concern.
        </Text>
        <Text>
          Individuals may also have the right to lodge a complaint with the
          Nigeria Data Protection Commission (NDPC), Nigeria's data-protection
          supervisory authority, where applicable.
        </Text>
        <Link
          href="https://www.ndpc.gov.ng"
          isExternal
          fontWeight="600"
          color="#087F73"
        >
          Nigeria Data Protection Commission
        </Link>
      </>
    ),
  },
  {
    number: "14",
    title: "Changes to this notice",
    content: (
      <>
        <Text>
          We may update this Privacy Notice when our website, activities,
          technologies, or applicable legal and regulatory requirements
          change.
        </Text>
        <Text>
          Where appropriate, we will indicate the date on which the notice was
          most recently updated. We encourage visitors to review this page
          periodically for changes.
        </Text>
      </>
    ),
  },
  {
    number: "15",
    title: "Contact",
    content: (
      <>
        <Text>
          For privacy questions, data-protection requests, or concerns about
          information handled through the Serena Space institutional website,
          please contact:
        </Text>

        <Stack spacing={1}>
          <Text fontWeight="600">Serena Space</Text>
          <Link
            href="mailto:support@serenaspace.com"
            fontWeight="600"
            color="#087F73"
          >
            support@serenaspace.com
          </Link>
        </Stack>

        <Text>
          Please do not send medical records, genetic information, passwords,
          payment-card information, or other highly sensitive material to this
          email address unless Serena Space has specifically instructed you to
          do so through an appropriate secure process.
        </Text>
      </>
    ),
  },
];

function PrivacyPage() {
  return (
    <Box bg="#F9FAF8">
      <Box
        bg="#17211F"
        color="white"
        py={{ base: 20, md: 28 }}
      >
        <Container maxW="1100px" px={{ base: 5, md: 8 }}>
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.16em"
            textTransform="uppercase"
            color="whiteAlpha.600"
            mb={5}
          >
            Privacy & Data Protection
          </Text>

          <Heading
            as="h1"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            lineHeight="1.05"
            letterSpacing="-0.045em"
            maxW="850px"
          >
            Responsible handling of information is part of how we build.
          </Heading>

          <Text
            mt={7}
            maxW="760px"
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.8"
            color="whiteAlpha.700"
          >
            Serena Space works across health science, technology, research and
            implementation. We take the privacy and responsible handling of
            personal information seriously, particularly where our work
            intersects with health and scientific data.
          </Text>

          <Text
            mt={8}
            fontSize="sm"
            color="whiteAlpha.500"
          >
            Effective date: September 2026
          </Text>
        </Container>
      </Box>

      <Container
        maxW="1100px"
        px={{ base: 5, md: 8 }}
        py={{ base: 16, md: 24 }}
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 12, lg: 20 }}>
          <Box>
            <Text
              fontSize="sm"
              lineHeight="1.8"
              color="gray.600"
              position={{ lg: "sticky" }}
              top={{ lg: "120px" }}
            >
              This notice describes the privacy practices relevant to the
              Serena Space institutional website. Product, research and
              health-data environments may have additional requirements and
              documentation.
            </Text>
          </Box>

          <Stack spacing={{ base: 14, md: 18 }}>
            {sections.map((section) => (
              <Box key={section.number}>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.14em"
                  color="#087F73"
                  mb={3}
                >
                  {section.number}
                </Text>

                <Heading
                  as="h2"
                  fontFamily="'Poppins', sans-serif"
                  fontSize={{ base: "xl", md: "2xl" }}
                  letterSpacing="-0.025em"
                  mb={5}
                >
                  {section.title}
                </Heading>

                <Stack
                  spacing={4}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="1.85"
                  color="gray.700"
                >
                  {section.content}
                </Stack>
              </Box>
            ))}
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  );
}

export default PrivacyPage;